if (!customElements.get('quick-order-list')) {
  customElements.define(
    'quick-order-list',
    class QuickOrderList extends BulkAdd {
      cartUpdateUnsubscriber = undefined;
      hasPendingQuantityUpdate = false;
      manualMode = false;
      modeHasBeenSelected = false;
      constructor() {
        super();
        this.isListInsideModal = this.closest('bulk-modal');

        this.handleToggleSummaryClick = this.handleToggleSummaryClick.bind(this);

        this.stickyHeaderElement = document.querySelector('sticky-header');
        if (this.stickyHeaderElement) {
          this.stickyHeader = {
            height: this.stickyHeaderElement.offsetHeight,
            type: `${this.stickyHeaderElement.getAttribute('data-sticky-type')}`,
          };
        }

        this.totalBar = this.getTotalBar();
        if (this.totalBar) {
          this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;

          this.handleResize = this.handleResize.bind(this);
          window.addEventListener('resize', this.handleResize);
        }

        this.querySelector('form').addEventListener('submit', (event) => event.preventDefault());
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          // skip if cart event was triggered by this section
          if (event.source === this.id) return;

          this.toggleTableLoading(true);
          await this.refresh();
          this.toggleTableLoading(false);
        });

        this.initToggleSummaryListener();
        this.initEventListeners();

        queueMicrotask(() => {
          if (!this.isConnected) return;
          this.resetQuantityInputs('page-load', true, false);
          this.setTableMaxHeight();
        });
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.quickOrderToggleSummary?.removeEventListener('click', this.handleToggleSummaryClick);
        window.removeEventListener('resize', this.handleResize);
      }

      handleResize() {
        this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;
        this.stickyHeader.height = this.stickyHeaderElement ? this.stickyHeaderElement.offsetHeight : 0;
        this.setTableMaxHeight();
      }

      initEventListeners() {
        this.querySelector('.quick-order-list__contents').addEventListener(
          'keyup',
          this.handleScrollIntoView.bind(this)
        );

        this.quickOrderListTable.addEventListener('keydown', this.handleSwitchVariantOnEnter.bind(this));

        this.initVariantEventListeners();
      }

      initToggleSummaryListener() {
        this.quickOrderToggle = this.closest('.quick-order-toggle');
        this.quickOrderToggleSummary = this.quickOrderToggle?.querySelector('.quick-order-toggle__summary');

        this.quickOrderToggleSummary?.addEventListener('click', this.handleToggleSummaryClick);
      }

      initVariantEventListeners() {
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));


        if (this.modeHasBeenSelected) {
          this.setTableLocked(!this.manualMode);
        }

        this.allInputsArray.forEach((input) => {
          const handleQuantityChange = () => {
            const totalQuantity = this.getRequestedQuantity();

            if (totalQuantity === 0) {
              this.setManualMode(false);
              this.updateAllocationMessage();
              return;
            }

            if (!this.manualMode) {
              this.setManualMode(true);
            }

            this.announceQuantities();
          };

          input.addEventListener('input', handleQuantityChange);
          input.addEventListener('change', handleQuantityChange);
        });
      }

      setManualMode(isManualMode) {
        this.manualMode = isManualMode;
        this.modeHasBeenSelected = true;
        this.setTableLocked(!isManualMode);
        this.dispatchEvent(
          new CustomEvent('quick-order-mode-change', {
            bubbles: true,
            detail: {
              mode: isManualMode ? 'table' : 'single',
              quantity: this.getRequestedQuantity(),
            },
          })
        );
      }

      get isManualMode() {
        return this.manualMode;
      }

      setTableLocked(isLocked) {
        if (!this.allInputsArray) return;

        this.allInputsArray.forEach((input) => {
          input.disabled = isLocked;
          input.classList.toggle('quantity__input--locked', isLocked);
        });
      }

      getRequestedQuantity() {
        return this.allInputsArray?.reduce((total, input) => total + (parseInt(input.value, 10) || 0), 0) || 0;
      }

      getCurrentAllocations() {
        return (
          this.allInputsArray?.reduce((map, input) => {
            const value = parseInt(input.value, 10) || 0;

            if (value > 0) {
              map[input.dataset.index] = value;
            }

            return map;
          }, {}) || {}
        );
      }

      announceQuantities() {
        this.dispatchEvent(
          new CustomEvent('quick-order-mode-change', {
            bubbles: true,
            detail: { mode: this.manualMode ? 'table' : 'single', quantity: this.getRequestedQuantity() },
          })
        );
      }

      get cartVariantsForProduct() {
        return JSON.parse(this.querySelector('[data-cart-contents]')?.innerHTML || '[]');
      }

      validateInput(target) {
        const targetValue = parseInt(target.value);
        const targetMin = parseInt(target.dataset.min);
        const targetStep = parseInt(target.step);

        if (target.max) {
          return (
            targetValue == 0 ||
            (targetValue >= targetMin && targetValue <= parseInt(target.max) && targetValue % targetStep == 0)
          );
        } else {
          return targetValue == 0 || (targetValue >= targetMin && targetValue % targetStep == 0);
        }
      }

      collectBulkUpdates() {
        const inputs = this.querySelectorAll('input[type="number"]');
        const updates = {};

        for (const input of inputs) {
          const id = input.dataset.index;
          const value = parseInt(input.value) || 0;

          // Validation example
          if (!this.validateInput(input)) {
            this.setErrorMessage(window.cartStrings.quantityError.replace('[quantity]', input.value));
            return { success: false, updates: {} };
          }

          updates[id] = value;
        }

        return { success: true, updates };
      }

      getUpdatesAndSend() {
        const { success, updates } = this.collectBulkUpdates();
        if (!success) return false; // stops ProductForm if validation fails

        this.updateMultipleQty(updates);
        return true;
      }

      get quickOrderListTable() {
        return this.querySelector('.quick-order-list__table');
      }

      get quickOrderListTableWrapper() {
        return this.querySelector('.quick-order-list__table-wrapper');
      }

      getSectionsToRender() {
        return [
          {
            id: this.id,
            section: this.dataset.section,
            selector: `#${this.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '#shopify-section-cart-icon-bubble',
          },
          {
            id: `quick-order-list-live-region-text-${this.dataset.productId}`,
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      toggleTableLoading(enable) {
        this.quickOrderListTable.classList.toggle('quick-order-list__container--disabled', enable);
        this.toggleLoading(enable);
      }

      async refresh() {
        const url = this.dataset.url || window.location.pathname;
        const currentQuantities = this.getQuantityInputValues();

        return fetch(`${url}?section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const responseQuickOrderList = html.querySelector(`#${this.id}`);

            if (!responseQuickOrderList) {
              return;
            }

            this.innerHTML = responseQuickOrderList.innerHTML;
            this.restoreQuantityInputValues(currentQuantities);
            this.initEventListeners();
            this.setTableMaxHeight();
          })
          .catch((e) => {
            console.error(e);
          });
      }

      renderSections(parsedState) {
        const { items, sections } = parsedState;

        this.getSectionsToRender().forEach(({ id, selector, section }) => {
          const sectionElement = document.getElementById(id);
          if (!sectionElement) return;

          const parsedSection = new DOMParser().parseFromString(sections[section], 'text/html');
          const newSection = parsedSection.querySelector(selector);

          if (!newSection) return;

          if (section === this.dataset.section) {
            if (this.queue.length > 0 || this.hasPendingQuantityUpdate) return;

            const focusedElement = document.activeElement;
            let focusTarget = focusedElement?.dataset?.target;
            if (focusTarget?.includes('remove')) {
              const quantityPopover = focusedElement.closest('quantity-popover');
              const incrementButton = quantityPopover?.querySelector('[data-target*="increment-"]');
              focusTarget = incrementButton?.dataset.target;
            }

            const total = this.getTotalBar();
            const newTotal = newSection.querySelector('.quick-order-list__total');
            if (total && newTotal) {
              total.innerHTML = newTotal.innerHTML;
            }

            const table = this.quickOrderListTable;
            const newTable = newSection.querySelector('.quick-order-list__table');

            if (newTable) {
              table.innerHTML = newTable.innerHTML;

              const newFocusTarget = this.querySelector(`[data-target='${focusTarget}']`);
              if (newFocusTarget) {
                newFocusTarget?.focus({ preventScroll: true });
              }

              this.initVariantEventListeners();
              this.setTableMaxHeight();
            }
          } else if (section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', items.length === 0);
            const cartDrawerSection = sectionElement.querySelector(selector);

            if (cartDrawerSection) {
              cartDrawerSection.innerHTML = newSection.innerHTML;
            }
          } else {
            sectionElement.innerHTML = newSection.innerHTML;
          }
        });
      }

      getTotalBar() {
        return this.querySelector('.quick-order-list__total');
      }

      setTableMaxHeight() {
        if (!this.quickOrderListTableWrapper || !this.quickOrderListTable) return;

        requestAnimationFrame(() => {
          if (!this.isConnected) return;

          const tableHead = this.quickOrderListTable.tHead;
          const tableBody = this.quickOrderListTable.tBodies?.[0];
          const rows = tableBody ? Array.from(tableBody.rows) : [];

          this.quickOrderListTableWrapper.style.removeProperty('--quick-order-list-max-height');
          this.quickOrderListTableWrapper.classList.remove('quick-order-list__table-wrapper--scrollable');

          if (!tableHead || rows.length <= 8) return;

          const headerHeight = tableHead.getBoundingClientRect().height || tableHead.offsetHeight || 0;
          const visibleRowsHeight = rows.slice(0, 8).reduce((totalHeight, row) => {
            return totalHeight + (row.getBoundingClientRect().height || row.offsetHeight || 0);
          }, 0);

          if (headerHeight === 0 || visibleRowsHeight === 0) return;

          this.quickOrderListTableWrapper.style.setProperty(
            '--quick-order-list-max-height',
            `${headerHeight + visibleRowsHeight}px`
          );
          this.quickOrderListTableWrapper.classList.add('quick-order-list__table-wrapper--scrollable');
        });
      }

      handleToggleSummaryClick(event) {
        if (!this.quickOrderToggle) return;

        const { top: initialTop } = this.quickOrderToggle.getBoundingClientRect();
        const initialScrollY = window.scrollY;

        event.preventDefault();
        this.quickOrderToggle.open = !this.quickOrderToggle.open;

        requestAnimationFrame(() => {
          this.setTableMaxHeight();

          if (this.isListInsideModal) return;

          const { top: newTop } = this.quickOrderToggle.getBoundingClientRect();
          const scrollAdjustment = newTop - initialTop;

          if (scrollAdjustment !== 0) {
            window.scrollTo({ top: window.scrollY + scrollAdjustment, left: window.scrollX });
          } else if (window.scrollY !== initialScrollY) {
            window.scrollTo({ top: initialScrollY, left: window.scrollX });
          }
        });
      }

      scrollTop() {
        const { top } = this.getBoundingClientRect();

        if (this.isListInsideModal) {
          this.scrollIntoView();
        } else {
          window.scrollTo({ top: top + window.scrollY - (this.stickyHeader?.height || 0), behavior: 'instant' });
        }
      }

      scrollQuickOrderListTable(target) {
        if (!this.totalBar) return;

        const inputTopBorder = target.getBoundingClientRect().top;
        const inputBottomBorder = target.getBoundingClientRect().bottom;

        if (this.isListInsideModal) {
          const totalBarCrossesInput = inputBottomBorder > this.totalBar.getBoundingClientRect().top;
          const tableHeadCrossesInput =
            inputTopBorder < this.querySelector('.quick-order-list__table thead').getBoundingClientRect().bottom;

          if (totalBarCrossesInput || tableHeadCrossesInput) {
            this.scrollToCenter(target);
          }
        } else {
          const stickyHeaderBottomBorder = this.stickyHeaderElement?.getBoundingClientRect().bottom;
          const totalBarCrossesInput = inputBottomBorder > this.totalBarPosition;
          const inputOutsideOfViewPort =
            inputBottomBorder < this.querySelector('.variant-item__quantity-wrapper').offsetHeight;
          const stickyHeaderCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type !== 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder;
          const stickyHeaderScrollupCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type === 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder &&
            stickyHeaderBottomBorder > 0;

          if (
            totalBarCrossesInput ||
            inputOutsideOfViewPort ||
            stickyHeaderCrossesInput ||
            stickyHeaderScrollupCrossesInput
          ) {
            this.scrollToCenter(target);
          }
        }
      }

      scrollToCenter(target) {
        target.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }

      handleScrollIntoView(event) {
        if ((event.key === 'Tab' || event.key === 'Enter') && this.allInputsArray.length !== 1) {
          this.scrollQuickOrderListTable(event.target);
        }
      }

      handleSwitchVariantOnEnter(event) {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;

        event.preventDefault();
        event.target.blur();

        if (!this.validateInput(event.target) || this.allInputsArray.length <= 1) return;

        const currentIndex = this.allInputsArray.indexOf(event.target);
        const offset = event.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + offset + this.allInputsArray.length) % this.allInputsArray.length;

        this.allInputsArray[nextIndex]?.select();
      }

      updateMultipleQty(items) {
        if (this.queue.length == 0) this.hasPendingQuantityUpdate = false;

        this.toggleLoading(true);
        const url = this.dataset.url || window.location.pathname;

        const cartQuantities = this.cartVariantsForProduct.reduce((map, item) => {
          map[item.variant_id] = item.quantity;
          return map;
        }, {});

        const updatesWithExistingQuantities = Object.entries(items).reduce((acc, [variantId, quantity]) => {
          const currentQuantity = parseInt(cartQuantities[variantId], 10) || 0;
          acc[variantId] = currentQuantity + quantity;
          return acc;
        }, {});

        const body = JSON.stringify({
          updates: updatesWithExistingQuantities,
          sections: this.getSectionsToRender().map(({ section }) => section),
          sections_url: url,
        });

        this.updateMessage();
        this.setErrorMessage();

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then(async (response) => {
            const state = await response.text();
            let parsedState = null;

            try {
              parsedState = JSON.parse(state);
            } catch (error) {
              throw new Error(window.cartStrings.error);
            }

            if (!response.ok || !parsedState || parsedState.status) {
              const message = parsedState?.description || parsedState?.message || window.cartStrings.error;
              throw new Error(message);
            }

            this.renderSections(parsedState);
            this.resetQuantityInputs('cart-update', true, false);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: this.id,
              cartData: parsedState,
            });
          })
          .catch((e) => {
            console.error(e);
            this.setErrorMessage(window.cartStrings.error);
          })
          .finally(() => {
            this.queue.length === 0 && this.toggleLoading(false);
            this.setRequestStarted(false);
          });
      }

      setErrorMessage(message = null) {
        this.errorMessageTemplate =
          this.errorMessageTemplate ??
          document.getElementById(`QuickOrderListErrorTemplate-${this.dataset.productId}`).cloneNode(true);
        const errorElements = document.querySelectorAll('.quick-order-list-error');

        errorElements.forEach((errorElement) => {
          errorElement.innerHTML = '';
          if (!message) return;
          const updatedMessageElement = this.errorMessageTemplate.cloneNode(true);
          updatedMessageElement.content.querySelector('.quick-order-list-error-message').innerText = message;
          errorElement.appendChild(updatedMessageElement.content);
        });
      }

      updateMessage(quantity = null) {
        const messages = this.querySelectorAll('.quick-order-list__message-text');
        const icons = this.querySelectorAll('.quick-order-list__message-icon');

        if (quantity === null || isNaN(quantity)) {
          messages.forEach((message) => (message.innerHTML = ''));
          icons.forEach((icon) => icon.classList.add('hidden'));
          return;
        }

        const isQuantityNegative = quantity < 0;
        const absQuantity = Math.abs(quantity);

        const textTemplate = isQuantityNegative
          ? absQuantity === 1
            ? window.quickOrderListStrings.itemRemoved
            : window.quickOrderListStrings.itemsRemoved
          : quantity === 1
          ? window.quickOrderListStrings.itemAdded
          : window.quickOrderListStrings.itemsAdded;

        messages.forEach((msg) => (msg.innerHTML = textTemplate.replace('[quantity]', absQuantity)));

        if (!isQuantityNegative) {
          icons.forEach((i) => i.classList.remove('hidden'));
        }
      }

      updateError(updatedValue, id) {
        let message = '';
        if (typeof updatedValue === 'undefined') {
          message = window.cartStrings.error;
        } else {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(id, message);
      }

      updateLiveRegions(id, message) {
        const variantItemStatus = this.querySelector('#shopping-cart-variant-item-status');
        if (variantItemStatus) {
          variantItemStatus.setAttribute('aria-hidden', true);
        }

        const cartStatus = document.getElementById('quick-order-list-live-region-text');
        if (!cartStatus) return;
        cartStatus.setAttribute('aria-hidden', false);

        setTimeout(() => {
          cartStatus.setAttribute('aria-hidden', true);
        }, 1000);
      }

      applyAllocation(allocation, messageText = '') {
        if (!allocation) return;

        this.allInputsArray?.forEach((input) => {
          const newValue = allocation[input.dataset.index] || 0;
          input.value = newValue;
          this.lockIncreaseIfFullyAllocated(input, newValue);
        });

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);

        this.setManualMode(false);
        this.updateAllocationMessage(messageText);
        this.announceQuantities();
      }

      lockIncreaseIfFullyAllocated(input, appliedQuantity) {
        const variantAvailabilityMap = this.getVariantAvailabilityMap();
        const availableQuantity = variantAvailabilityMap?.[input.dataset.index];

        if (typeof availableQuantity !== 'number') return;

        if (appliedQuantity >= availableQuantity) {
          input.max = appliedQuantity;
          return;
        }

        input.max = availableQuantity;
      }

      getVariantAvailabilityMap() {
        if (this.variantAvailabilityMap) return this.variantAvailabilityMap;

        try {
          const variants = JSON.parse(this.dataset.variantData || '[]');
          this.variantAvailabilityMap = variants.reduce((map, variant) => {
            map[variant.id] = Math.max(0, parseInt(variant.inventory_quantity, 10) || 0);
            return map;
          }, {});
        } catch (error) {
          this.variantAvailabilityMap = {};
        }

        return this.variantAvailabilityMap;
      }

      updateAllocationMessage(messageText = '') {
        const messageTargets = this.querySelectorAll('.quick-order-list__message-text');
        const icons = this.querySelectorAll('.quick-order-list__message-icon');
        const wrappers = this.querySelectorAll('.quick-order-list__message');

        const hasMessage = Boolean(messageText);

        messageTargets.forEach((message) => {
          message.textContent = messageText;
        });

        icons.forEach((icon) => icon.classList.toggle('hidden', !hasMessage));
        wrappers.forEach((wrapper) => wrapper.classList.toggle('hidden', !hasMessage));
      }

      resetQuantityInputs(reason = '', shouldNotify = true, shouldAnnounce = true) {
        const inputs = this.querySelectorAll('input[type="number"]');

        inputs.forEach((input) => {
          input.value = 0;
          input.setAttribute('value', 0);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        if (this.manualMode) {
          this.manualMode = false;
          this.modeHasBeenSelected = true;
          this.setTableLocked(true);
        }

        if (shouldAnnounce) {
          this.announceQuantities();
        }

        this.updateAllocationMessage();

        if (shouldNotify) {
          this.dispatchEvent(
            new CustomEvent('quick-order-quantities-reset', {
              bubbles: true,
              detail: { reason },
            })
          );
        }
      }

      getQuantityInputValues() {
        return Array.from(this.querySelectorAll('input[type="number"]')).reduce((quantities, input) => {
          quantities[input.dataset.index] = input.value;
          return quantities;
        }, {});
      }

      restoreQuantityInputValues(quantityMap = {}) {
        this.querySelectorAll('input[type="number"]').forEach((input) => {
          const previousValue = quantityMap[input.dataset.index];
          if (typeof previousValue === 'undefined') return;

          input.value = previousValue;
          input.setAttribute('value', previousValue);
        });
      }

      toggleLoading(loading, target = this) {
        target.querySelector('#shopping-cart-variant-item-status').toggleAttribute('aria-hidden', !loading);
        const spinners = target.querySelectorAll('.variant-remove-total .loading__spinner');
        spinners?.forEach((spinner) => spinner.classList.toggle('hidden', !loading));
      }
    }
  );
}
